import { type AssessmentRecord, FINDINGS, REVIEW_STATES, type ReviewState } from '@fruuit/shared'
import {
  Button,
  Callout,
  type Column,
  DataTable,
  EmptyState,
  Input,
  PageHeader,
  Select,
  StatusBadge,
} from '@fruuit/ui'
import { ClipboardCheck, Plus, Search } from 'lucide-react'
import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { errorMessage } from '../../api/client'
import { AssessmentWorkbench } from '../../features/records/AssessmentWorkbench'
import { useClients, useRecordList } from '../../features/records/api'
import { formatDate, vocabOptions } from '../../features/records/format'
import { useDebouncedValue } from '../../features/records/useDebouncedValue'
import { useTitle } from '../../lib/useTitle'

const PAGE_SIZE = 25

/** S13 Assessments list (R-06): versions, filters and the way into the workbench. */
export function AssessmentsPage() {
  useTitle('Assessments')
  const navigate = useNavigate()
  const [state, setState] = useState<ReviewState | ''>('')
  const [client, setClient] = useState('')
  const [finding, setFinding] = useState('')
  const [term, setTerm] = useState('')
  const q = useDebouncedValue(term, 300)
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined])
  const cursor = cursors[cursors.length - 1]
  const clients = useClients()

  const query = {
    limit: PAGE_SIZE,
    ...(cursor ? { cursor } : {}),
    ...(state ? { state } : {}),
    ...(client ? { client_org_id: client } : {}),
    ...(finding ? { finding: finding as (typeof FINDINGS.values)[number] } : {}),
    ...(q ? { q } : {}),
  }
  const list = useRecordList('assessments', query)
  const rows = list.data?.items ?? []

  function resetPaging() {
    setCursors([undefined])
  }

  const columns: Column<AssessmentRecord>[] = [
    {
      id: 'record',
      header: 'Assessment',
      cell: (row) => (
        <div className="min-w-0">
          <p className="font-semibold text-ink">{row.issuer_name ?? row.issuer_id}</p>
          <p className="text-xs text-sage">
            Version {row.version}
            {row.client_org_id ? ' · client-specific' : ' · shared'}
          </p>
        </div>
      ),
    },
    {
      id: 'finding',
      header: 'Conclusion',
      cell: (row) => (
        <div className="flex flex-col items-start gap-1">
          <StatusBadge status={row.finding} />
          <span className="text-xs text-sage">{row.priority}</span>
        </div>
      ),
    },
    {
      id: 'state',
      header: 'Workflow',
      cell: (row) => <StatusBadge status={row.state} />,
    },
    {
      id: 'reason',
      header: 'Reason for this version',
      className: 'max-w-[22rem]',
      cell: (row) => (
        <span className="line-clamp-2 text-sm text-ink-soft">{row.change_reason ?? '—'}</span>
      ),
    },
    {
      id: 'updated',
      header: 'Updated',
      cell: (row) => <span className="whitespace-nowrap">{formatDate(row.updated_at)}</span>,
    },
  ]

  const filtered = Boolean(state || client || finding || q)

  return (
    <>
      <PageHeader
        eyebrow="Fruuit research"
        title="Signals become reviewed findings."
        description="Retrieval signals, incidents and assessments remain distinct. Company responses do not replace Fruuit's judgment."
        actions={
          <Button icon={<Plus />} onClick={() => navigate('/workspace/assessments/new')}>
            New assessment
          </Button>
        }
      />
      <Callout className="mb-6">
        Draft → in review → approved → published. Released versions are immutable: a correction is a
        new version linked to the one it corrects.
      </Callout>
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1.4fr)_repeat(3,minmax(0,1fr))]">
        <div className="relative">
          <Input
            label="Search"
            type="search"
            placeholder="Company name"
            value={term}
            onChange={(event) => {
              setTerm(event.target.value)
              resetPaging()
            }}
            inputClassName="pl-9"
          />
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute bottom-3.5 left-3 size-4 text-sage"
          />
        </div>
        <Select
          label="Workflow state"
          value={state}
          onChange={(event) => {
            setState(event.target.value as ReviewState | '')
            resetPaging()
          }}
          placeholder="All states"
          options={vocabOptions(REVIEW_STATES)}
        />
        <Select
          label="Conclusion"
          value={finding}
          onChange={(event) => {
            setFinding(event.target.value)
            resetPaging()
          }}
          placeholder="All conclusions"
          options={vocabOptions(FINDINGS)}
        />
        <Select
          label="Client context"
          value={client}
          onChange={(event) => {
            setClient(event.target.value)
            resetPaging()
          }}
          placeholder="All clients"
          options={[
            { value: 'shared', label: 'Shared with all subscribers' },
            ...(clients.data?.items ?? []).map((org) => ({ value: org.id, label: org.name })),
          ]}
        />
      </div>
      {list.isError ? (
        <Callout tone="danger" role="alert" className="mb-4">
          {errorMessage(list.error)}
        </Callout>
      ) : null}
      <DataTable
        caption="Assessment versions"
        columns={columns}
        rows={rows}
        getRowId={(row) => row.id}
        loading={list.isLoading}
        onRowClick={(row) => navigate(`/workspace/assessments/${row.id}`)}
        empty={
          <EmptyState
            variant="plain"
            icon={<ClipboardCheck />}
            title={filtered ? 'No records match this search' : 'No assessments yet'}
            description={
              filtered
                ? 'Change or clear the filters to see more versions.'
                : 'No records yet. Add the first record to start this workflow.'
            }
          />
        }
        pagination={{
          mode: 'server',
          hasPrevious: cursors.length > 1,
          hasNext: Boolean(list.data?.next_cursor),
          onPrevious: () => setCursors((current) => current.slice(0, -1)),
          onNext: () => {
            const next = list.data?.next_cursor
            if (next) setCursors((current) => [...current, next])
          },
          summary: `Page ${cursors.length} · ${rows.length} ${rows.length === 1 ? 'version' : 'versions'}`,
        }}
      />
    </>
  )
}

/** `/workspace/assessments/new` and `/workspace/assessments/:id`. */
export function AssessmentWorkbenchPage() {
  const { id } = useParams()
  const [params] = useSearchParams()
  const isNew = !id || id === 'new'
  return (
    <AssessmentWorkbench
      key={isNew ? 'new' : id}
      {...(isNew ? {} : { id })}
      issuerId={params.get('issuer')}
    />
  )
}
